import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { translateText } from '~/common/components/TranslatedLabel/TranslatedLabel';

interface LoadingEllipsesProps {
  className?: string;
  timeout?: number;
  ellipsesCount?: number;
}

const ELLIPSES_DELIMITER = '.';

export const LoadingEllipses = ({
  className = '',
  timeout = 500,
  ellipsesCount = 3,
}: LoadingEllipsesProps) => {
  // Subscribe to i18n so imperative `translateText(...)` updates after locale changes.
  useTranslation(['common']);
  const [activeIndex, setActiveIndex] = useState(0);

  const dotKeys = useMemo(
    () => Array.from({ length: ellipsesCount }, (_, i) => `ellipses-dot-${i}`),
    [ellipsesCount]
  );

  // Every timeout interval, update the active dot index.
  // Show the dots up to the active index. E.g., if active index is 2, show 3 dots.
  // Use opacity to show/hide dots for smooth transition and to avoid layout shift.
  useEffect(() => {
    const interval = setInterval(() => {
      setActiveIndex((prev) => (prev + 1) % ellipsesCount);
    }, timeout);

    return () => clearInterval(interval);
  }, [timeout, ellipsesCount]);

  return (
    <div
      className={className}
      role="status"
      aria-label={translateText('common:loading')}
    >
      {dotKeys.map((dotKey, index) => (
        <span
          key={dotKey}
          style={{ opacity: index <= activeIndex ? 1 : 0 }}
          aria-hidden
        >
          {ELLIPSES_DELIMITER}
        </span>
      ))}
    </div>
  );
};
